import { FormEvent, useEffect, useRef, useState } from "react";
import { Loader2, RotateCcw, Send, Sparkles, X } from "lucide-react";
import { api, errorMessage, post } from "@/lib/api";
import { CONTEXT_WINDOW_DEFAULT } from "@/lib/assistantThread";
import AssistantReview, { type AssistantPatch } from "@/components/courses/AssistantReview";

type ChatRole = "user" | "assistant";

type CategoryProposed = {
  title: string;
  type?: string;
};

interface ChatMessage {
  role: ChatRole;
  content: string;
  sources?: AssistantSource[];
  staged?: number;
  taxonomyProposed?: string[];
  categoryProposed?: CategoryProposed[];
}

interface AssistantSource {
  kind: "library" | "web";
  id?: string;
  title?: string;
  url?: string;
}

interface AssistantApplied {
  tool: string;
  label: string;
  id?: string;
}

interface AssistantReply {
  reply: string;
  sources?: AssistantSource[];
  pending?: AssistantPatch[];
  applied?: AssistantApplied[];
  taxonomyProposed?: string[];
  categoryProposed?: CategoryProposed[];
  usedWeb?: boolean;
  usedLibrary?: boolean;
  wrote?: boolean;
  staged?: boolean;
  rolledOver?: boolean;
}

interface ApplyReply {
  applied?: AssistantApplied[];
  idMap?: Record<string, string>;
  taxonomyProposed?: string[];
  categoryProposed?: CategoryProposed[];
  error?: string;
  incomplete?: boolean;
}

interface ThreadGroup {
  seq: number;
  messages: ChatMessage[];
  live?: boolean;
}

interface ThreadView {
  rememberHistory?: boolean;
  groups?: ThreadGroup[];
  messages?: ChatMessage[];
  canContinue?: boolean;
  windowSize?: number;
  remaining?: number;
}

function remapPatches(rows: AssistantPatch[], idMap: Record<string, string>) {
  return rows.map((row) => {
    const input = { ...row.input };
    if (input.parentId && idMap[input.parentId]) input.parentId = idMap[input.parentId];
    if (input.itemId && idMap[input.itemId]) input.itemId = idMap[input.itemId];
    return { ...row, input };
  });
}

function categoryProposedLine(row: CategoryProposed) {
  const kind =
    row.type === "concentration" ? "Concentration" : row.type === "subject" ? "Core" : "category";
  return `Recommended ${kind}: ${row.title} — not in your catalog.`;
}

function sourceLabel(src: AssistantSource) {
  return src.title?.trim() || src.url || (src.kind === "library" ? "Library source" : "Web page");
}

function cascadeReject(pending: AssistantPatch[], patchId: string) {
  const drop = new Set([patchId]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const row of pending) {
      if (drop.has(row.patchId)) continue;
      const refs = [row.input?.parentId, row.input?.itemId];
      if (refs.some((id) => id && drop.has(id))) {
        drop.add(row.patchId);
        grew = true;
      }
    }
  }
  return pending.filter((row) => !drop.has(row.patchId));
}

function ChatBubble({
  row,
  onReview,
}: {
  row: ChatMessage;
  onReview?: () => void;
}) {
  return (
    <article className={`al-cb-asst-msg al-cb-asst-msg--${row.role}`}>
      <p>{row.content}</p>
      {row.staged ? (
        <button type="button" className="al-cb-asst-open" onClick={onReview}>
          Review {row.staged} change{row.staged === 1 ? "" : "s"}
        </button>
      ) : null}
      {row.sources?.length ? (
        <details className="al-cb-asst-src">
          <summary>
            {row.sources.length} source{row.sources.length === 1 ? "" : "s"}
          </summary>
          <ul>
            {row.sources.map((src, j) => (
              <li key={`${src.kind}-${src.id || src.url || j}`}>
                {src.url ? (
                  <a href={src.url} target="_blank" rel="noreferrer">
                    {sourceLabel(src)}
                  </a>
                ) : (
                  sourceLabel(src)
                )}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
      {row.categoryProposed?.length ? (
        <ul className="al-cb-asst-propose">
          {row.categoryProposed.map((item, j) => (
            <li key={`${item.title}-${j}`}>{categoryProposedLine(item)}</li>
          ))}
        </ul>
      ) : null}
      {row.taxonomyProposed?.length ? (
        <p className="al-cb-asst-web">Standard not in the catalog: {row.taxonomyProposed.join(", ")}</p>
      ) : null}
    </article>
  );
}

export default function CourseAssistant({
  courseId,
  canWrite,
  rememberHistory,
  focusItemId,
  open,
  onOpen,
  onClose,
  onCourseChanged,
}: {
  courseId: string;
  canWrite: boolean;
  rememberHistory: boolean;
  focusItemId?: string | null;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  onCourseChanged: () => Promise<void> | void;
}) {
  const [archived, setArchived] = useState<ChatMessage[][]>([]);
  const [live, setLive] = useState<ChatMessage[]>([]);
  const [pending, setPending] = useState<AssistantPatch[]>([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [applying, setApplying] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [threadReady, setThreadReady] = useState(false);
  const [sendContext, setSendContext] = useState(false);
  const [promptOpen, setPromptOpen] = useState(false);
  const [freshNotice, setFreshNotice] = useState(!rememberHistory);
  const [windowSize, setWindowSize] = useState(CONTEXT_WINDOW_DEFAULT);
  const [error, setError] = useState("");
  const scroller = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setPending([]);
    setReviewing(false);
    setError("");
    setDraft("");
    setSendContext(false);
    setArchived([]);
    setLive([]);
    setPromptOpen(false);
    setFreshNotice(!rememberHistory);
    let cancelled = false;
    setThreadReady(false);
    (async () => {
      try {
        const envelope = await api<{ success: boolean; data: ThreadView }>(
          `/api/courses/${encodeURIComponent(courseId)}/assistant`,
        );
        if (cancelled) return;
        const data = envelope.data || {};
        if (data.windowSize) setWindowSize(data.windowSize);
        if (!rememberHistory) {
          setArchived([]);
          setLive([]);
          setPromptOpen(false);
          setFreshNotice(true);
          return;
        }
        const groups = Array.isArray(data.groups) ? data.groups : [];
        const closed = groups.filter((g) => !g.live).map((g) => g.messages);
        const openGroup = groups.find((g) => g.live);
        const fallback = !groups.length && Array.isArray(data.messages) ? data.messages : [];
        const liveRows = openGroup?.messages || fallback;
        setArchived(closed);
        setLive(liveRows);
        setPromptOpen(Boolean(data.canContinue && liveRows.length));
        setFreshNotice(!liveRows.length && closed.length > 0);
      } catch {
        if (!cancelled) {
          setArchived([]);
          setLive([]);
        }
      } finally {
        if (!cancelled) setThreadReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [courseId, rememberHistory]);

  useEffect(() => {
    const el = scroller.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [archived, live, busy, open, pending, promptOpen]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const archiveLive = (rows: ChatMessage[]) => {
    if (!rows.length) return;
    setArchived((prev) => [...prev, rows]);
  };

  const startNewWindow = async (rowsToArchive: ChatMessage[]) => {
    archiveLive(rowsToArchive);
    setLive([]);
    setSendContext(false);
    setPromptOpen(false);
    setFreshNotice(true);
    await post(`/api/courses/${encodeURIComponent(courseId)}/assistant/context`, {
      action: "fresh",
    }).catch(() => {});
  };

  const send = async (event?: FormEvent) => {
    event?.preventDefault();
    const text = draft.trim();
    if (!text || busy || applying || !threadReady) return;
    setDraft("");
    setError("");

    let resetContext = false;
    let continueContext = sendContext;
    let historySource = live;

    if (promptOpen) {
      resetContext = true;
      continueContext = false;
      archiveLive(live);
      historySource = [];
      setLive([]);
      setPromptOpen(false);
      setFreshNotice(true);
      setSendContext(false);
    } else if (live.length >= windowSize) {
      resetContext = true;
      continueContext = false;
      archiveLive(live);
      historySource = [];
      setFreshNotice(true);
      setSendContext(false);
    }

    const userRow: ChatMessage = { role: "user", content: text };
    const nextLive = [...(resetContext ? [] : historySource), userRow];
    setLive(nextLive);
    setBusy(true);
    try {
      const envelope = await post<{ success: boolean; data: AssistantReply }>(
        `/api/courses/${encodeURIComponent(courseId)}/assistant`,
        {
          message: text,
          history: rememberHistory
            ? []
            : nextLive.slice(0, -1).map((row) => ({
                role: row.role,
                content: row.content,
                sources: row.sources,
              })),
          focus: focusItemId ? { itemId: focusItemId } : undefined,
          continueContext,
          resetContext,
        },
      );
      const data = envelope.data;
      const staged = data?.pending || [];
      const assistantRow: ChatMessage = {
        role: "assistant",
        content: data?.reply || "Done.",
        sources: data?.sources || [],
        staged: staged.length,
        taxonomyProposed: data?.taxonomyProposed || [],
        categoryProposed: data?.categoryProposed || [],
      };
      const combined = [...nextLive, assistantRow];
      if (combined.length >= windowSize || data?.rolledOver) {
        setArchived((groups) => [...groups, combined]);
        setLive([]);
        setFreshNotice(true);
        setSendContext(false);
      } else {
        setLive(combined);
        setFreshNotice(false);
      }
      if (staged.length) {
        setPending((prev) => [...prev, ...staged]);
        setReviewing(true);
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
      inputRef.current?.focus();
    }
  };

  const applyPatches = async (rows: AssistantPatch[]) => {
    if (!rows.length || applying || busy) return;
    setError("");
    setApplying(true);
    try {
      const envelope = await post<{ success: boolean; data: ApplyReply }>(
        `/api/courses/${encodeURIComponent(courseId)}/assistant/apply`,
        { patches: rows },
      );
      const idMap = envelope.data?.idMap || {};
      const appliedIds = new Set(Object.keys(idMap));
      const left = remapPatches(pending.filter((row) => !appliedIds.has(row.patchId)), idMap);
      setPending(left);
      if (envelope.data?.error) setError(envelope.data.error);
      else if (!left.length) setReviewing(false);
      if (appliedIds.size) await onCourseChanged();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setApplying(false);
    }
  };

  const rejectOne = (patchId: string) => {
    const left = cascadeReject(pending, patchId);
    const dropped = pending.length - left.length;
    setPending(left);
    if (!left.length) setReviewing(false);
    if (dropped) {
      void post(`/api/courses/${encodeURIComponent(courseId)}/assistant/outcome`, {
        action: "rejected",
        count: dropped,
      }).catch(() => {});
    }
  };

  if (!open) {
    return (
      <button type="button" className="al-cb-asst-fab" aria-label="Assistant" title="Assistant" onClick={onOpen}>
        <Sparkles size={18} aria-hidden="true" />
        {pending.length ? <span className="al-cb-asst-fab-count">{pending.length}</span> : null}
      </button>
    );
  }

  const locked = busy || applying || !threadReady;
  const canClear = !locked && (live.length > 0 || promptOpen);
  const showPrompt = promptOpen && rememberHistory && live.length > 0;
  const remaining = Math.max(0, windowSize - live.length);
  const emptyLive = live.length === 0;

  return (
    <aside className="al-cb-asst" aria-label="Course assistant">
      <header className="al-cb-asst-head">
        <div>
          <p className="al-cb-asst-kicker">
            <Sparkles size={14} aria-hidden="true" />
            Assistant
          </p>
          <p className="al-cb-asst-sub">
            Prefers the library, then the web.{" "}
            {canWrite
              ? "Drafts readings, homework, quizzes, exams, and questions — pick what to keep in the review panel."
              : "Read-only — drafts in chat only."}{" "}
            No images, video, or slides yet.
          </p>
        </div>
        <div className="al-cb-asst-head-tools">
          <button
            type="button"
            className="al-cb-icon-btn"
            aria-label="Start a new thread"
            title="Start a new thread"
            disabled={!canClear}
            onClick={() => void startNewWindow(live)}
          >
            <RotateCcw size={14} />
          </button>
          <button type="button" className="al-cb-icon-btn" aria-label="Close assistant" onClick={onClose}>
            <X size={16} />
          </button>
        </div>
      </header>

      <div className="al-cb-asst-log" ref={scroller}>
        {archived.map((group, i) => (
          <details key={`archived-${i}`} className="al-cb-asst-group">
            <summary>
              Earlier thread — not sent to the assistant ({group.length} message
              {group.length === 1 ? "" : "s"})
            </summary>
            <div className="al-cb-asst-group-body">
              {group.map((row, j) => (
                <ChatBubble key={`${row.role}-${j}`} row={row} />
              ))}
            </div>
          </details>
        ))}

        {showPrompt ? (
          <div className="al-cb-asst-prompt">
            <p>
              Previous messages are not sent unless you continue. You still have room in this
              thread ({live.length} of {windowSize}). Continue with the assistant, or start new?
            </p>
            <div className="al-cb-asst-prompt-actions">
              <button
                type="button"
                className="al-cb-btn"
                disabled={locked}
                onClick={() => {
                  setSendContext(true);
                  setPromptOpen(false);
                  setFreshNotice(false);
                }}
              >
                Continue thread
              </button>
              <button
                type="button"
                className="al-cb-btn al-cb-btn--ghost"
                disabled={locked}
                onClick={() => void startNewWindow(live)}
              >
                Start new
              </button>
            </div>
          </div>
        ) : freshNotice || (!rememberHistory && emptyLive) ? (
          <p className="al-cb-asst-fresh">
            {archived.length
              ? "This is the start of a new thread — the assistant does not see the messages above."
              : "This is the start of a new thread — nothing previously sent is remembered by the assistant."}
          </p>
        ) : sendContext && rememberHistory && live.length > 0 ? (
          <p className="al-cb-asst-fresh">
            Continuing this thread ({remaining} message{remaining === 1 ? "" : "s"} of context left).
          </p>
        ) : null}

        {!threadReady ? (
          <p className="al-cb-asst-busy">
            <Loader2 size={14} className="al-cb-spin" aria-hidden="true" />
            Loading thread…
          </p>
        ) : emptyLive && !showPrompt ? (
          <div className="al-cb-asst-empty">
            <p>Ask for a reading, key terms, homework, a quiz, an exam, a discussion, or a unit outline.</p>
            <p>Example: “From the library, add a short reading and a 5-question homework check for the open lesson.”</p>
          </div>
        ) : (
          live.map((row, i) => (
            <ChatBubble key={`${row.role}-${i}`} row={row} onReview={() => setReviewing(true)} />
          ))
        )}
        {busy ? (
          <p className="al-cb-asst-busy">
            <Loader2 size={14} className="al-cb-spin" aria-hidden="true" />
            Working…
          </p>
        ) : null}
      </div>

      {pending.length && canWrite ? (
        <div className="al-cb-asst-pending">
          <p>
            {pending.length} change{pending.length === 1 ? "" : "s"} waiting
          </p>
          <button type="button" className="al-cb-btn" disabled={locked} onClick={() => setReviewing(true)}>
            {applying ? <Loader2 size={14} className="al-cb-spin" aria-hidden="true" /> : null}
            Review
          </button>
        </div>
      ) : null}

      {error && !reviewing ? <p className="al-cb-asst-error">{error}</p> : null}

      <form className="al-cb-asst-form" onSubmit={(e) => void send(e)}>
        <textarea
          ref={inputRef}
          className="al-cb-textarea"
          rows={3}
          value={draft}
          disabled={locked}
          placeholder={canWrite ? "What should we add to this course?" : "Ask about this course…"}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
        />
        <button type="submit" className="al-cb-btn" disabled={locked || !draft.trim()}>
          <Send size={14} aria-hidden="true" />
          Send
        </button>
      </form>

      <AssistantReview
        open={reviewing && pending.length > 0 && canWrite}
        patches={pending}
        applying={applying}
        error={error}
        onClose={() => setReviewing(false)}
        onReject={rejectOne}
        onApply={(rows) => void applyPatches(rows)}
      />
    </aside>
  );
}
